/* eslint-disable */
import React, { useState, useEffect, useContext } from 'react';
import styles from '../Skillset/skillset.module.scss';
import EditIcon from '../../../../../Assets/images/svg/dar_pencil.svg';
import ButtonComponent from '../../../../../Components/Button/button';
import NoDataFound from '../../../../../Assets/images/svg/dar_no_new_file.svg';
import MagnifyingGlass from '../../../../../Assets/images/svg/dar_magnifying.svg';
import DataGridComponent from '../../../Dashboard/Efforts/scopeDataGrid';
import { TextField } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import collapseIcon from '../../../../../Assets/images/svg/dar_purple_dropdown.svg';
import SearchDropdown from '../../../../../Components/Dropdown/dropdown';
import UseAxios from '../../../../../Api/http/http';
import { deleteSkillsetData, getSkillsetData, putSkillsetData } from '../../../../../Api/request/dealEffort';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from '../../../../../Components/Snackbar/snackBarProvider';
import { getStaffingCostOption } from '../../../../../Api/request/costMaster';
import { AuthContext } from '../../../../../Contexts/AuthContext';
import { MODULE_NAMES } from '../../../../../Constants/modules';

const Skillset = () => {
  const [rows, setRows] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [rowId, setRowId] = useState(null);
  const [roles, setRoles] = useState([]);
  const { snackbarOpen } = useSnackbar();

  const [searchParams] = useSearchParams();
  const DEAL_ID = parseInt(searchParams.get('id'), 10);
  const { userProfile } = useContext(AuthContext);
  const permission = userProfile?.permissions
    ? userProfile.permissions[MODULE_NAMES.DEAL_TRANSITION_EFFORT_ESTIMATION] || []
    : [];

  const hasAccess = (action) => {
    return permission.includes(action);
  };

  const handleRowEnter = (e) => {
    const rowId = Number(e.currentTarget.dataset.id);
    setRowId(rowId);
  };

  const handleRowOut = () => {
    setRowId(null);
  };

  const handleCollapse = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id], // Toggle the expanded state
    }));
  };

  const locationOptions = [
    { id: 1, name: 'Onsite' },
    { id: 2, name: 'Offsite' },
  ];

  const experienceOptions = [
    { id: 1, name: 'Junior' },
    { id: 2, name: 'Mid' },
    { id: 3, name: 'Senior' },
  ];

  const fetchRolesData = async () => {
    try {
      const response = await UseAxios(getStaffingCostOption());
      const { role } = response.data.data;
      const roleData = role.map((role) => ({ ...role, id: role.type_id, name: role.resource_type}));
      setRoles(roleData || []);
    } catch (error) {
      console.error('Error fetching roles options:', error.message || error);
      snackbarOpen('Oops! Please try again', 'warning');
    }
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  const getTaskName = (scope) => {
    switch (scope.type) {
      case 'FE':
        return 'Front End Development';
      case 'BE':
        return 'Back End Development';
      default:
        return scope.task;
    }
  };

  const fetchSkillsetDetails = async () => {
    try { // API call to fetch skillSet Details
      const response = await UseAxios(getSkillsetData(DEAL_ID));
      const transformedRows = response?.data?.data.list.map((scope, scopeIndex) => ({
        id: scope.scopeId,
        srNo: scopeIndex + 1,
        task: getTaskName(scope),
        items: scope.items?.map(item => ({
          ...item,
          experience: capitalizeFirstLetter(item.experience),
          location: capitalizeFirstLetter(item.location),
          count: parseFloat(item.count),
        })) || [],
        isEditing: false,
      }));
      setRows(transformedRows);
    } catch (error) {
      console.error('Error fetching skillSet details:', error.message || error);
      snackbarOpen('Oops! Please try again', 'warning');
    }
  };

  useEffect(() => {
    fetchRolesData();
    fetchSkillsetDetails();
  }, [DEAL_ID]);

  useEffect(() => {
    if (isEdit) {
      setIsEdit(false);
    }
  }, [rows]);

  const handleSearch = (event) => {
    setSearchText(event.target.value.toLowerCase());
  };

  const filteredRows = searchText
    ? rows.filter(row =>
      row.task?.toLowerCase().includes(searchText.toLowerCase())
      || row.items?.some(item =>
        ['skillset', 'experience', 'location', 'count', 'remarks'].some(field => {
          const value = item[field];
          return String(value || '').toLowerCase()
            .includes(searchText.toLowerCase());
        }),
      ),
    )
    : rows;

  const updateItems = (items, itemId, field, value) => {
    return items.map(item =>
      item.id === itemId ? { ...item, [field]: value } : item,
    );
  };

  const handleInputChange = (e, rowId, itemId, field) => {
    const value = e.target.value?.name || e.target.value;
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id !== rowId) return row;
        return {
          ...row,
          items: updateItems(row.items, itemId, field, value),
        };
      }),
    );
  };

  const handleSave = async (id) => {
    const rowToSave = rows.find((row) => row.id === id);
    const scopeId = rowToSave.id;
    const itemsToSave = rowToSave.items;
    const payload = itemsToSave.map(item => ({
      ...(typeof item.id === 'number' && { deal_skill_id: item.id }), // Only send the id if it's a number
      skillset_id: roles.find((role) => role.name === item.skillset)?.id,
      experience: item.experience,
      location: item.location,
      count: item.count,
      remarks: item.remarks,
    }));
    try {
      await UseAxios(putSkillsetData(scopeId, payload));
      snackbarOpen('Thank you! Your information has been successfully saved.', 'success');
    } catch (error) {
      console.error('Error updating row:', error.message || error);
      snackbarOpen('Oops! Please try again', 'warning');
      return;
    }
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === id ? { ...row, isEditing: false, isEdit: false } : row,
      ),
    );
    fetchSkillsetDetails();
  };

  const getUpdatedRow = (row, originalData) => {
    if (row.isEdit) {
      return {
        ...originalData,
        isEditing: false,
        isEdit: false,
      };
    }
    return {
      ...row,
      items: row.items.filter(item => typeof item.id === 'number'),
      isEditing: false,
    };
  };

  const handleCancel = (id) => {
    setRows(prevRows => {
      const targetRow = prevRows.find(r => r.id === id);
      if (!targetRow) return prevRows;
      return prevRows.map(row => {
        if (row.id !== id) return row;
        return getUpdatedRow(row, row.originalData);
      });
    });
  };

  const handleEdit = (id) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === id
          ? { ...row, isEditing: true, isEdit: true, originalData: { ...row } }
          : row,
      ),
    );
  };

  const newRowTemplate = {
    skillset: '',
    location: '',
    experience: '',
  };

  const isRowUnchanged = (row) => {
    return row.items.some(item =>
      item.skillset === newRowTemplate.skillset
        || item.location === newRowTemplate.location
        || item.experience === newRowTemplate.experience,
    );
  };

  const updateCountValue = (item, itemId, countValue) => {
    if (item.id !== itemId) return item;
    if (countValue === '' || countValue.endsWith('.')) {
      return { ...item, count: countValue };
    }
    const value = parseFloat(countValue);
    if (!isNaN(value) && value >= 0 && value <= 999) {
      return { ...item, count: Math.round(value * 10) / 10 };
    }
    return item;
  };

  const updateRowItems = (row, rowId, itemId, countValue) => {
    if (row.id !== rowId) return row;
    return {
      ...row,
      items: row.items.map(item => updateCountValue(item, itemId, countValue)),
    };
  };

  const handleCountChange = (countValue, rowId, itemId) => {
    setRows(prevRows =>
      prevRows.map(row => updateRowItems(row, rowId, itemId, countValue)),
    );
  };

  const updateItemValue = (items, itemId, field, isIncrement) => {
    return items.map(item => {
      if (item.id !== itemId) return item;
      const currentValue = parseFloat(item[field]);
      const newValue = isIncrement
        ? Math.min(currentValue + 1, 999)
        : Math.max(currentValue - 1, 0.1);
      return { ...item, [field]: Math.round(newValue * 10) / 10 };
    });
  };

  const handleDecrement = (rowId, itemId, field) => {
    setRows(prevRows =>
      prevRows.map(row =>
        row.id === rowId
          ? { ...row, items: updateItemValue(row.items, itemId, field, false) }
          : row,
      ),
    );
  };

  const handleIncrement = (rowId, itemId, field) => {
    setRows(prevRows =>
      prevRows.map(row =>
        row.id === rowId
          ? { ...row, items: updateItemValue(row.items, itemId, field, true) }
          : row,
      ),
    );
  };

  const getSelectedValue = (name, options) => {
    return options.find(option => option.name === name) || null;
  };

  const getDisplayItems = (items, rowId, expandedRows, field) => {
    if (!items.length) {
      return '-';
    }
    const itemsToDisplay = expandedRows[rowId] ? items : items.slice(0, 1);
    return itemsToDisplay.map((item) => (
      <div className={styles.taskItems} key={item.id}>
        {item?.[field] || '-'}
      </div>
    ));
  };

  const columns = [
    {
      field: 'srNo',
      headerName: 'Sr. No.',
      minWidth: 90,
      maxWidth: 90,
      flex: 0.1,
      sortable: false,
      disableColumnMenu: true,
      cellClassName: styles.srNoCell,
    },
    {
      field: 'task',
      headerName: 'Task',
      minWidth: 200,
      flex: 0.8,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
        params.row.isEditing ? (
          <div className={styles.taskDiv}>
            <div className={styles.saveCancelOuterDiv}>
              <label className={styles.taskLabel}>{params.row.task}</label>
            </div>
          </div>
        ) : (
          <label className="saved-value">{params.row.task}</label>
        ),
    },
    {
      field: '',
      headerName: '',
      minWidth: 58,
      width: 58,
      maxWidth: 58,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
        params.row.isEditing ? (
          <div></div>
        ) : (
          <label className={styles.addRemovebtnOuterDiv}>
            {(expandedRows[params.row.id]
              ? params.row.items : params.row.items.slice(0, 1)).map((item, index, array) => (
              <div className={styles.addRemoveBtnDiv} key={`hoverBtn-${item.id}`}>
                {rowId == params.row.id && (
                  <>
                    {index === array.length - 1 && (
                      <ButtonComponent
                        variant="outlined"
                        onClick={() => handleAddRow(params.row.id)}
                        startIcon={<AddIcon />}
                        btnCss={styles.addBtn}
                        disabled={!hasAccess('create')}
                      />
                    )}
                    <ButtonComponent
                      variant="outlined"
                      onClick={() => handleDeleteRow(params.row.id, item.id)}
                      startIcon={<RemoveIcon />}
                      btnCss={styles.addBtn}
                      disabled={!hasAccess('delete')}
                    />
                  </>
                )}
              </div>
            ))}
          </label>
        ),
    },
    {
      field: 'skillset',
      headerName: 'Role',
      minWidth: 145,
      flex: 0.4,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
        params.row.isEditing ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {params.row.items.map((item) => (
              <SearchDropdown
                key={`skillset-${item.id}`}
                searchBox={false}
                placeholder='Select'
                options={roles}
                onChange={(e) => handleInputChange(e, params.row.id, item.id, 'skillset')}
                value={getSelectedValue(item.skillset, roles)}
                dropdownCss={styles.textbox}
              />
            ))}
          </div>
        ) : (
          <label className="saved-value">
            {getDisplayItems(params.row.items, params.row.id, expandedRows, 'skillset')}
          </label>
        ),
    },
    {
      field: 'experience',
      headerName: 'Experience',
      minWidth: 105,
      flex: 0.3,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
        params.row.isEditing ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {params.row.items.map((item) => (
              <SearchDropdown
                key={`experience-${item.id}`}
                searchBox={false}
                placeholder='Select'
                dropdownCss={styles.textbox}
                options={experienceOptions}
                onChange={(e) => handleInputChange(e, params.row.id, item.id, 'experience')}
                value={getSelectedValue(item.experience, experienceOptions)}
              />
            ))}
          </div>
        ) : (
          <label className="saved-value">
            {getDisplayItems(params.row.items, params.row.id, expandedRows, 'experience')}
          </label>
        ),
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 0.3,
      minWidth: 105,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) =>
        params.row.isEditing ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {params.row.items.map((item) => (
              <SearchDropdown
                key={`location-${item.id}`}
                searchBox={false}
                options={locationOptions}
                placeholder='Select'
                onChange={(e) => handleInputChange(e, params.row.id, item.id, 'location')}
                value={getSelectedValue(item.location, locationOptions)}
                dropdownCss={styles.textbox}
              />
            ))}
          </div>
        ) : (
          <label className="saved-value">
            {getDisplayItems(params.row.items, params.row.id, expandedRows, 'location')}
          </label>
        ),
    },
    {
      field: 'count',
      headerName: 'Count',
      minWidth: 100,
      flex: 0.3,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return params.row.isEditing ? (
          <div>
            {params.row.items.map((item) => (
              <div className={styles.counterDiv} key={`count-${item.id}`}>
                <div key={item.id} style={{ display: 'flex', alignItems: 'center' }}>
                  <button
                    className={styles.counterButtonDec}
                    style={{marginBottom: '3px'}}
                    onClick={() => handleDecrement(params.row.id, item.id, 'count')}
                  >
                    –
                  </button>
                  <input
                    type="text"
                    value={item.count}
                    onChange={(e) => handleCountChange(e.target.value, params.row.id, item.id)}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      const finalValue = value === '0' || value === '0.' || value === ''
                        ? '1' : parseFloat(value).toFixed(1);
                      handleCountChange(finalValue, params.row.id, item.id);
                    }}
                  />
                  <button
                    className={styles.counterButtonInc}
                    onClick={() => handleIncrement(params.row.id, item.id, 'count')}
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <label className="saved-value">
            {getDisplayItems(params.row.items, params.row.id, expandedRows, 'count')}
          </label>
        );
      },
    },
    {
      field: 'remarks',
      headerName: 'Remark',
      minWidth: 270,
      flex: 0.95,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
        params.row.isEditing ? (
          <div>
            {params.row.items.map((item) => (
              <TextField
                key={item.id}
                defaultValue={item.remarks}
                onChange={(e) => handleInputChange(e, params.row.id, item.id, 'remarks')}
                onKeyDown={(e) => e.stopPropagation()}
                className={styles.remarksTextfield}
                variant="outlined"
                size="small"
              />
            ))}
          </div>
        ) : (
          <label className="saved-value">
            {getDisplayItems(params.row.items, params.row.id, expandedRows, 'remarks')}
          </label>
        ),
    },
    {
      headerClassName: styles.actionColumn,
      cellClassName: styles.actionCell,
      maxWidth: 85,
      renderCell: (params) =>
        params.row.id === '' ? (
          <div></div>
        ) : (
          <div className={styles.mobileActionIcons}>
            {params.row?.items?.length != 0 ? (
              <>
                {!params.row?.isEditing ? (
                  <button
                    className="role-action-edit-icon"
                    aria-label="Edit"
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: hasAccess('update') ? 'pointer' : 'not-allowed',
                      opacity: hasAccess('update') ? 1 : 0.5,
                    }}
                    onClick={() => handleEdit(params.row.id)}
                    disabled={!hasAccess('update')}
                  >
                    <img src={EditIcon} alt="Edit" />
                  </button>
                ):(
                  <div className={styles['save-and-cancel-button-main']} style={{ paddingTop: '0px' }}>
                  <button
                    className={styles['save-and-cancel-button'] + ' ' + styles['save-button-tick-placing']}
                    style={{
                      backgroundColor: isRowUnchanged(params.row) ? '#a69df1' : '#6B5CE8', 
                      color: '#ffff',
                    }}
                    onClick={() => handleSave(params.row.id)}
                    disabled={isRowUnchanged(params.row)}
                  >✔</button>
                  <button
                    className={styles['save-and-cancel-button'] + ' ' + styles['save-button-cross-placing']}
                    style={{
                      backgroundColor: 'transparent',
                      color: '#6B5CE8',
                      border: '1px solid #6B5CE8'
                    }}
                    onClick={() => handleCancel(params.row.id)}
                  >X</button>
                </div>
                )}
              </>
            ) : (
              <ButtonComponent
                variant="outlined"
                onClick={() => handleAddRow(params.row.id)}
                startIcon={<AddIcon />}
                btnCss={`${styles.addBtn} ${styles.actionAddBtn}`}
                disabled={!hasAccess('create')}
              />
            )}
            {params.row?.items?.length > 1 && !params.row?.isEditing && (
              <button
                className="role-action-edit-icon"
                aria-label="Collapse"
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                }}
                onClick={() => handleCollapse(params.row.id)}
              >
                <img src={collapseIcon}
                  style={{ transform: expandedRows[params.row.id] ? 'rotate(180deg)' : 'none' }} alt="Edit" />
              </button>
            )}
          </div>
        ),
      sortable: false,
      disableColumnMenu: true,
    },
  ];

  const handleAddRow = (rowId) => {
    const newItem = {
      id: uuidv4(),
      skillset: '',
      count: 1,
      location: 'Offsite',
      experience: '',
      remarks: '',
    };

    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id === rowId) {
          return {
            ...row,
            items: [...row.items, newItem],
            isEditing: true,
          };
        }
        return row;
      }),
    );
  };

  const filterItems = (items, itemId) => {
    return items.filter(item => item.id !== itemId);
  };

  const handleDeleteRow = async (rowId, itemId) => {
    try {
      await UseAxios(deleteSkillsetData(itemId)); // API call to delete item
    } catch (error) {
      console.error('Error deleting row:', error.message || error);
      snackbarOpen('Oops! Please try again', 'warning');
      return;
    }
    setRows(prevRows =>
      prevRows.map(row =>
        row.id === rowId
          ? { ...row, items: filterItems(row.items, itemId) }
          : row,
      ),
    );
  };

  return (
    <div className={styles.skillsetCostMainDiv}>
      <div className={styles.skillsetCcostSummaryDiv2}>
        <div className={styles.skillsetCostMain}>
          <span>Required Skillset</span>
          <div className={styles.skillsetCostSearchAdd}>
            <div className={styles.SkillsetCostSearch}>
              <img src={MagnifyingGlass} alt="Search" />
              <input
                id="skillset-cost-search-input"
                type="text"
                placeholder="Search..."
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={styles.skillsetCostGrid}>
        <DataGridComponent
          dataRows={filteredRows}
          dataColumns={columns}
          pageSize={10}
          isEdit={setRows}
          isFilteredRows={filteredRows}
          sortFilterColumns={[]}
          onRowSelectionChange={() => {}}
          checkboxDisabled={false}
          rowHeight={150}
          noDataImage={NoDataFound}
          noDataTitle="No Skillset added yet."
          noDataSubTitle=""
          hideSeparator={true}
          dataGridCss={styles.dataGrid}
          getRowHeight={() => 'auto'}
          handleRowEnter={handleRowEnter}
          handleRowLeave={handleRowOut}
        />
      </div>
    </div>
  );
};

export default Skillset;
